/**
 * Plausible custom event tracking for the chatbot widget.
 *
 * Events are only delivered when the Plausible script is loaded
 * (i.e. `trackingEnabled` is true on the tenant and the page is not in
 * preview mode). `trackPlausibleEvent` is a no-op otherwise.
 */

import { trackPlausibleEvent } from '../../utilities/track-plausible-event';

/**
 * SessionStorage key used to mark the next Plausible event as originating
 * from a chatbot link click. The `customProperties` callback in the Plausible
 * init reads and removes this key, adding `source: "chatbot"` to the event.
 */
export const CHATBOT_REFERRER_KEY = 'plausible_chatbot_referrer';

/**
 * Mark the target URL as referred from the chatbot.
 * The URL is normalised to a pure pathname (no query/hash) so it can be
 * compared against `window.location.pathname` in the Plausible callback.
 */
export function markChatbotReferrer(url: string): void {
  if (typeof window === 'undefined') return;
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    sessionStorage.setItem(CHATBOT_REFERRER_KEY, pathname);
  } catch {
    // sessionStorage or URL parsing may be unavailable
  }
}

type ChatbotEventMap = {
  'Chatbot Open': undefined;
  'Chatbot Close': undefined;
  'Chatbot Message Sent': { message_count: number };
  'Chatbot Response Complete': { message_count: number };
  'Chatbot Navigate': { url: string; label: string };
  'Chatbot Navigation Feedback': { url: string; feedback: string };
  'Chatbot Link Click': { url: string; source: string };
  'Chatbot New Thread': undefined;
  'Chatbot Error': undefined;
};

type ChatbotEventName = keyof ChatbotEventMap;

export function trackChatbotEvent<K extends ChatbotEventName>(
  event: K,
  ...args: ChatbotEventMap[K] extends undefined ? [] : [ChatbotEventMap[K]]
): void {
  trackPlausibleEvent(event, args[0]);
}
